import React from 'react';
import { Paper } from '@mui/material';
import { makeStyles, GridSize } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import { Card, CardHeader, CardFooter } from "@backstage/ui";
import { Page, Header, Content } from '@backstage/core-components';
import { configApiRef, useApi } from '@backstage/core-plugin-api';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';

import { pagesTranslationRef} from '../../translations/pages';
import LinkArrow, { LinkArrowHoverStyle } from '../LinkArrow';


const useStyles = makeStyles(() => ({
    flexStart: {
        alignItems: 'flex-start',
    },
    flexStretch: {
        alignItems: 'stretch',
    },
    itemLink: {
        display: 'block',
        height: '100%',
        position: 'relative',
        borderRadius: '1rem',
        clipPath: 'rect(auto auto auto auto round 1rem)',
        '&:hover div:last-child': {
            opacity: 1,
        },
    },
    itemLinkOverlay: {
        transition: 'opacity 0.2s',
        display: 'inline-block',
        borderRadius: '3rem',
        opacity: 0,
        fontWeight: 500,
        position: 'absolute',
        left: '50%',
        top: '50%',
        padding: '0.625rem 1.25rem',
        background: 'white',
        transform: 'translate(-50%, -50%)',
        zIndex: 1,
        boxShadow: '0px 0px 133px 140px hsl(from #0bacf3 calc(h + 0) calc(s + 0) calc(l + 0) / 0.92)',
        pointerEvents: 'none',
    },
    itemPaper: {
        height: '100%',
        fontWeight: 500,
        fontSize: '1.125rem',
        color: '#0e151d',
        backgroundColor: '#f7f8f9',
        boxShadow: 'none',
        borderRadius: '1rem',
        padding: '1rem 1rem 0.75rem 1rem',
        display: 'flex',
        flexDirection: 'column',
        rowGap: '0.5rem',
        transition: 'background-color 300ms',
        '& div:first-child svg:last-child': {
            float: 'right',
        },
        '& div:first-child': {
            fontSize: '1rem',
            fontWeight: 500,
        },
        '&:hover svg': LinkArrowHoverStyle,
        '&:hover': {
            backgroundColor: 'white',
        },
        '& div:last-child': {
            fontSize: 'clamp(0.625rem, 1.0vw, 1rem)',
            fontWeight: 'normal',
            lineHeight: '1.375rem',
        },
    },
    sideCard: {
        paddingTop: '0',
        borderRadius: '1rem',
        textAlign: 'left',
        gap: 0,
    },
    sideHeader: {
        padding: '0.625rem',
        color: 'white',
        textAlign: 'left',
        backgroundColor: '#0d151d',
        fontWeight: 500,
        fontSize: 'clamp(1rem, 1.2vw, 1.125rem)',
        letterSpacing: '-0.05rem',
    },
    sideFooter: {
        textAlign: 'right',
        '& a': {
            color: '#004c88',
            padding: '0.5rem',
            border: '0.0625rem solid #004c88',
            borderRadius: '0.25rem',
            fontSize: '1rem',
            fontWeight: 500,
            textTransform: 'uppercase',
        },
    },
}));


export const SideFooter = ({children, className}: {className?: string, children: React.ReactNode}) => {
    const classes = useStyles();
    return (
        <CardFooter className={className !== undefined ? `${className} ${classes.sideFooter}` : classes.sideFooter}>
            {children}
        </CardFooter>
    );
};


export const SideGridItem = ({
    header,
    children,
    md,
}: {
    header: string,
    children: React.ReactNode,
    md?: GridSize,
}) => {
    const classes = useStyles();
    return (
        <Grid item xs={12} md={md || 4}>
            <Card className={classes.sideCard}>
                <CardHeader className={classes.sideHeader}>
                    {header}
                </CardHeader>
                {children}
            </Card>
        </Grid>
    );
}


const GridItem = ({
    title,
    link,
    linkText,
    children,
    md,
}: {
    title: string;
    link: string;
    linkText: string;
    children: React.ReactNode;
    md?: GridSize;
}) => {
    const classes = useStyles();
    return (
        <Grid item xs={12} md={md || 4}>
            <a href={link} target='_blank' className={classes.itemLink}>
                <Paper className={classes.itemPaper}>
                    <div>
                        {title}
                        <LinkArrow/>
                    </div>
                    <div>{children}</div>
                </Paper>
                <div className={classes.itemLinkOverlay}>{linkText}</div>
            </a>
        </Grid>
    );
}


export default ({titleKey, area, side}: {titleKey: string, area: string, side?: React.ReactNode}) => {
    const classes = useStyles();
    const configApi = useApi(configApiRef);
    const { t } = useTranslationRef(pagesTranslationRef);
    const keySuffix = t('pages.keySuffix');

    const items = [];
    for(const cfg of configApi.getConfigArray(`${area}.items`)) {
        const title = cfg.getString('title' + keySuffix);
        const text = cfg.getString('text' + keySuffix);
        const linkText = cfg.getString('linkText' + keySuffix);
        const url = cfg.getString('url');
        items.push({title, text, linkText, url});
    }
    const itemsMd = side ? 6 : 12;
    const itemMd = side ? 6 : 4;
    const sideMd = items.length > 0 ? 6 : 12;
    return (
        <Page themeId='tool'>
            {/* @ts-ignore */}
            <Header title={t(titleKey)} />
            <Content>
                <Grid container spacing={3} className={classes.flexStart}>
                    {items.length > 0 && (
                        <Grid item container xs={12} md={itemsMd} className={classes.flexStretch}>
                            {items.map(({title, text, linkText, url}) => (
                                <GridItem title={title} link={url} linkText={linkText} md={itemMd}>
                                    {text}
                                </GridItem>
                            ))}
                        </Grid>
                    )}
                    {side && (
                        <Grid item container xs={12} md={sideMd}>
                            <Grid item xs={12} md={12}>
                                {side}
                            </Grid>
                        </Grid>
                    )}
                </Grid>
            </Content>
        </Page>
    );
};
